import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import api from "./api";

// Components
import OldDashboard from "./components/old/Dashboard";
import OldAccountView from "./components/old/AccountView";
import AddHolding from "./components/old/AddHolding";
import Roadmap from "./components/Roadmap";
import OldTickerDetailsPage from "./components/old/OldTickerDetailsPage";
import Landing from "./components/Landing";
import Login from "./components/Login";
import VerifyMagicLink from "./components/VerifyMagicLink";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Contact from "./components/Contact";
import NewDashboardLayout from "./components/layout/NewDashboardLayout";
import AdminPanel from "./components/admin/AdminPanel";
import AdminLayout from "./components/admin/AdminLayout";
import DatabaseAdmin, {
  DatabaseAdminPanel,
} from "./components/admin/DatabaseAdmin";
import FinancialChatUI from "./components/FinancialChatUI";
import Profile from "./components/Profile";
import { NotificationProvider } from "./components/NotificationContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// The /accounts/summary endpoint returns nested rows: { account: {...Account},
// total_value, cash_balance, portfolio_value, todays_change, todays_change_percent }.
// Flatten to a single account object so the whole app consumes one consistent flat
// shape (acc.id, acc.sync_in_progress, acc.total_value, ...). The summary aggregates
// win over the cached Account fields for display. Previously the nested shape made
// acc.id undefined downstream (broken sidebar links, dead sync-status polling).
const flattenAccountSummary = (rows) =>
  (rows || []).map((row) => {
    const account = row.account || row;
    return {
      ...account,
      total_value: row.total_value ?? account.total_value,
      cash_balance: row.cash_balance ?? account.cash_balance,
      portfolio_value: row.portfolio_value ?? account.portfolio_value,
      todays_change: row.todays_change,
      todays_change_percent: row.todays_change_percent,
    };
  });

function AppContent() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      // Use the new /accounts/summary endpoint that returns everything in one call
      const response = await api.get("/accounts/summary");
      setAccounts(flattenAccountSummary(response.data));
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh accounts without showing loading spinner (for updates)
  const refreshAccounts = async () => {
    try {
      // Use the same /accounts/summary endpoint
      console.log("🔄 Refreshing accounts...");
      const response = await api.get("/accounts/summary");
      console.log("✅ Accounts refreshed:", response.data);
      setAccounts(flattenAccountSummary(response.data));
      console.log("✅ Accounts state updated");
    } catch (error) {
      console.error("Error refreshing accounts:", error);
    }
  };

  // Only fetch accounts when user is authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchAccounts();
    } else if (!authLoading) {
      // User is not authenticated, stop loading
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const createAccount = async (accountData) => {
    try {
      const response = await api.post("/accounts", accountData);
      // Don't fetch accounts here - let the modal handle it when it's ready to close
      return response.data;
    } catch (error) {
      console.error("Error creating account:", error);
      throw error;
    }
  };

  const deleteAccount = async (accountId) => {
    try {
      await api.delete(`/accounts/${accountId}`);
      await fetchAccounts();
    } catch (error) {
      console.error("Error deleting account:", error);
      throw error;
    }
  };

  // Show loading spinner while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {isAuthenticated ? (
            // AUTHENTICATED ROUTES - All share the same NewDashboardLayout instance
            <>
              {/* Main dashboard routes */}
              <Route
                path="/"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />
              <Route
                path="/:tab"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />
              <Route
                path="/account/:accountId"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />
              <Route
                path="/account/:accountId/:tab"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />
              <Route
                path="/ticker/:symbol"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />

              {/* Intel Library route */}
              <Route
                path="/intel"
                element={
                  <NewDashboardLayout
                    accounts={accounts}
                    loading={loading}
                    onAccountCreated={createAccount}
                    onRefresh={refreshAccounts}
                  />
                }
              />

              {/* Profile route */}
              <Route path="/profile" element={<Profile />} />

              {/* Legacy routes */}
              <Route
                path="/old"
                element={
                  <OldDashboard
                    accounts={accounts}
                    loading={loading}
                    onSelectAccount={setSelectedAccount}
                    onCreateAccount={createAccount}
                    onDeleteAccount={deleteAccount}
                    onRefresh={fetchAccounts}
                  />
                }
              />
              <Route
                path="/old/account/:id"
                element={
                  <OldAccountView
                    accounts={accounts}
                    onRefresh={fetchAccounts}
                  />
                }
              />
              <Route
                path="/old/account/:accountId/ticker/:symbol"
                element={<OldTickerDetailsPage accounts={accounts} />}
              />
              <Route
                path="/old/account/:id/add-holding"
                element={<AddHolding onRefresh={fetchAccounts} />}
              />

              {/* Chat routes */}
              <Route path="/chat" element={<FinancialChatUI />} />
              <Route path="/chat/:accountId" element={<FinancialChatUI />} />

              {/* Roadmap */}
              <Route path="/roadmap" element={<Roadmap />} />

              {/* Admin routes - check if user is admin */}
              {user?.is_admin ? (
                <>
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminPanel />} />
                    <Route path="db" element={<DatabaseAdminPanel />} />
                    <Route path=":table" element={<AdminPanel />} />
                    <Route path=":table/:recordId" element={<AdminPanel />} />
                  </Route>
                  <Route path="/dbadmin" element={<DatabaseAdmin />} />
                </>
              ) : (
                <Route path="/admin/*" element={<Navigate to="/" replace />} />
              )}

              {/* Public routes */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/contact" element={<Contact />} />

              {/* Catch-all for unknown authenticated routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            // UNAUTHENTICATED ROUTES
            <>
              {/* Landing page */}
              <Route path="/" element={<Landing />} />

              {/* Auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/verify" element={<VerifyMagicLink />} />

              {/* Public routes */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/contact" element={<Contact />} />

              {/* Redirect all other routes to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <AppContent />
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
